/**
 * Display formatting options for report columns.
 *
 * Provides presets for:
 * - Date/time formatting
 * - Number formatting (decimals, thousands separator)
 * - Currency formatting (USD, CAD)
 */

import type { DateGroupingInterval } from './aggregation-types';

// --- Types ---

export type DateFormatPreset =
  | 'MM/DD/YYYY'
  | 'MM-DD-YYYY'
  | 'DD/MM/YYYY'
  | 'YYYY-MM-DD'
  | 'MMM DD, YYYY'
  | 'MMMM DD, YYYY';

export type TimeFormatPreset = 'none' | 'HH:mm' | 'HH:mm:ss' | 'hh:mm A' | 'hh:mm:ss A';

export type CurrencyCode = 'none' | 'USD' | 'CAD';

export interface DateFormatConfig {
  type: 'date';
  dateFormat: DateFormatPreset;
  timeFormat: TimeFormatPreset;
}

export interface NumberFormatConfig {
  type: 'number';
  decimals: number;
  useThousandsSeparator: boolean;
  currency: CurrencyCode;
  divisor?: number;
}

export interface UrlFormatConfig {
  type: 'url';
  adminPathTemplate: string; // path template used in tio-admin
  employeePathTemplate: string; // path template used in tio-employee
}

export type AppContext = 'admin' | 'employee';

export type ColumnFormatConfig = DateFormatConfig | NumberFormatConfig | UrlFormatConfig;

/** Map of column key → format config */
export type ColumnFormatMap = Record<string, ColumnFormatConfig>;

// --- Presets ---

export const DATE_FORMAT_OPTIONS: Array<{ key: DateFormatPreset; label: string }> = [
  { key: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { key: 'MM-DD-YYYY', label: 'MM-DD-YYYY' },
  { key: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { key: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  { key: 'MMM DD, YYYY', label: 'MMM DD, YYYY' },
  { key: 'MMMM DD, YYYY', label: 'MMMM DD, YYYY' },
];

export const TIME_FORMAT_OPTIONS: Array<{ key: TimeFormatPreset; label: string }> = [
  { key: 'none', label: 'No time' },
  { key: 'HH:mm', label: 'HH:mm (24h)' },
  { key: 'HH:mm:ss', label: 'HH:mm:ss (24h)' },
  { key: 'hh:mm A', label: 'hh:mm AM/PM' },
  { key: 'hh:mm:ss A', label: 'hh:mm:ss AM/PM' },
];

export const DECIMAL_OPTIONS: Array<{ key: string; label: string }> = [
  { key: '0', label: '0 decimals' },
  { key: '1', label: '1 decimal' },
  { key: '2', label: '2 decimals' },
  { key: '3', label: '3 decimals' },
  { key: '4', label: '4 decimals' },
];

export const CURRENCY_OPTIONS: Array<{ key: CurrencyCode; label: string }> = [
  { key: 'none', label: 'No currency' },
  { key: 'USD', label: 'USD ($)' },
  { key: 'CAD', label: 'CAD (C$)' },
];

// --- Default configs ---

export function getDefaultDateFormat(): DateFormatConfig {
  return { type: 'date', dateFormat: 'MM/DD/YYYY', timeFormat: 'none' };
}

export function getDefaultNumberFormat(): NumberFormatConfig {
  return { type: 'number', decimals: 0, useThousandsSeparator: true, currency: 'none' };
}

export function getDefaultUrlFormat(): UrlFormatConfig {
  return { type: 'url', adminPathTemplate: '/:value', employeePathTemplate: '/:value' };
}

// --- Formatting functions ---

const MONTH_NAMES_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const MONTH_NAMES_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Format a grouped date bucket value according to the grouping interval.
 * OpenSearch key_as_string formats: year→"yyyy", quarter→"yyyy-QQQ",
 * month→"yyyy-MM", week/day→"yyyy-MM-dd". We parse these string formats
 * directly rather than relying on new Date() which can't handle all of them.
 */
function formatGroupedDate(raw: unknown, interval: DateGroupingInterval): string {
  const str = String(raw);

  switch (interval) {
    case 'year': {
      const match = str.match(/^(\d{4})$/);
      return match ? match[1]! : str;
    }
    case 'quarter': {
      const match = str.match(/^(\d{4})-Q(\d)$/);
      return match ? `Q${match[2]} ${match[1]}` : str;
    }
    case 'month': {
      const match = str.match(/^(\d{4})-(\d{2})$/);
      if (match) {
        const monthIndex = parseInt(match[2]!, 10) - 1;
        return `${MONTH_NAMES_SHORT[monthIndex]} ${match[1]}`;
      }
      return str;
    }
    case 'week': {
      const date =
        typeof raw === 'string' || typeof raw === 'number' ? new Date(raw) : null;
      if (date && !isNaN(date.getTime())) {
        const m = date.getUTCMonth();
        return `Week of ${MONTH_NAMES_SHORT[m]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
      }
      return str;
    }
    case 'day':
    default:
      break;
  }

  return '';
}

/**
 * Format a raw value as a date string using the specified format config.
 * When groupingInterval is provided (date field in group-by mode),
 * formats according to the interval granularity instead.
 */
export function formatDateValue(
  raw: unknown,
  config: DateFormatConfig,
  groupingInterval?: DateGroupingInterval
): string {
  if (raw === null || raw === undefined || raw === '') return '—';

  if (groupingInterval && groupingInterval !== 'day') {
    const grouped = formatGroupedDate(raw, groupingInterval);
    if (grouped) return grouped;
  }

  const date = typeof raw === 'string' || typeof raw === 'number' ? new Date(raw) : null;
  if (!date || isNaN(date.getTime())) return String(raw);

  // Use UTC getters for date-only strings (YYYY-MM-DD) to avoid timezone shift
  const isDateOnly = typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw);
  const year = isDateOnly ? date.getUTCFullYear() : date.getFullYear();
  const month = isDateOnly ? date.getUTCMonth() : date.getMonth(); // 0-indexed
  const day = isDateOnly ? date.getUTCDate() : date.getDate();
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');

  let datePart: string;
  switch (config.dateFormat) {
    case 'MM/DD/YYYY':
      datePart = `${mm}/${dd}/${year}`;
      break;
    case 'MM-DD-YYYY':
      datePart = `${mm}-${dd}-${year}`;
      break;
    case 'DD/MM/YYYY':
      datePart = `${dd}/${mm}/${year}`;
      break;
    case 'YYYY-MM-DD':
      datePart = `${year}-${mm}-${dd}`;
      break;
    case 'MMM DD, YYYY':
      datePart = `${MONTH_NAMES_SHORT[month]} ${dd}, ${year}`;
      break;
    case 'MMMM DD, YYYY':
      datePart = `${MONTH_NAMES_FULL[month]} ${dd}, ${year}`;
      break;
    default:
      datePart = `${mm}/${dd}/${year}`;
  }

  if (config.timeFormat === 'none') return datePart;

  const hours24 = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  let timePart: string;
  switch (config.timeFormat) {
    case 'HH:mm':
      timePart = `${String(hours24).padStart(2, '0')}:${minutes}`;
      break;
    case 'HH:mm:ss':
      timePart = `${String(hours24).padStart(2, '0')}:${minutes}:${seconds}`;
      break;
    case 'hh:mm A': {
      const period = hours24 >= 12 ? 'PM' : 'AM';
      const h12 = hours24 % 12 || 12;
      timePart = `${String(h12).padStart(2, '0')}:${minutes} ${period}`;
      break;
    }
    case 'hh:mm:ss A': {
      const period = hours24 >= 12 ? 'PM' : 'AM';
      const h12 = hours24 % 12 || 12;
      timePart = `${String(h12).padStart(2, '0')}:${minutes}:${seconds} ${period}`;
      break;
    }
    default:
      timePart = '';
  }

  return timePart ? `${datePart} ${timePart}` : datePart;
}

/**
 * Format a raw value as a number string using the specified format config.
 */
export function formatNumberValue(raw: unknown, config: NumberFormatConfig): string {
  if (raw === null || raw === undefined || raw === '') return '—';

  const num = typeof raw === 'number' ? raw : Number(raw);
  if (isNaN(num)) return String(raw);

  const divided = config.divisor && config.divisor !== 0 ? num / config.divisor : num;
  const rounded = Number(divided.toFixed(config.decimals));

  let formatted: string;
  if (config.useThousandsSeparator) {
    formatted = rounded.toLocaleString('en-US', {
      minimumFractionDigits: config.decimals,
      maximumFractionDigits: config.decimals,
    });
  } else {
    formatted = rounded.toFixed(config.decimals);
  }

  if (config.currency === 'USD') {
    return `$${formatted}`;
  } else if (config.currency === 'CAD') {
    return `C$${formatted}`;
  }

  return formatted;
}

/**
 * Build a URL by interpolating :value in the path template with the cell value.
 * Uses adminPathTemplate or employeePathTemplate based on the appContext.
 * Returns { href, label } for link rendering.
 */
export function buildUrlValue(
  raw: unknown,
  config: UrlFormatConfig,
  appContext: AppContext = 'admin'
): { href: string; label: string } | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const template =
    appContext === 'employee' ? config.employeePathTemplate : config.adminPathTemplate;
  if (!template) return null;
  const cellValue = encodeURIComponent(String(raw));
  let path = template.replace(/:value/g, cellValue);
  // Ensure path starts with / to produce valid URLs
  if (!path.startsWith('/')) path = `/${path}`;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return { href: `${origin}${path}`, label: String(raw) };
}

/**
 * Apply column formatting to a raw cell value.
 * Returns null if no format is configured for this column.
 * For URL formats, returns the string label — use buildUrlValue() for link data.
 */
export function applyColumnFormat(
  raw: unknown,
  format: ColumnFormatConfig | undefined,
  groupingInterval?: DateGroupingInterval
): string | null {
  if (!format) return null;

  if (format.type === 'date') {
    return formatDateValue(raw, format, groupingInterval);
  }
  if (format.type === 'number') {
    return formatNumberValue(raw, format);
  }
  if (format.type === 'url') {
    // For plain-text contexts, return the label; link rendering is handled separately
    if (raw === null || raw === undefined || raw === '') return '—';
    return String(raw);
  }

  return null;
}

/**
 * Determine if a field type supports formatting.
 */
export function supportsFormatting(
  fieldType: string,
  reportDatatype?: string
): 'date' | 'number' | null {
  const type = (reportDatatype || fieldType || '').toLowerCase();

  if (type === 'date' || type === 'datetime' || type === 'timestamp' || type.includes('date')) {
    return 'date';
  }

  if (
    type === 'integer' ||
    type === 'long' ||
    type === 'float' ||
    type === 'double' ||
    type === 'number' ||
    type === 'currency' ||
    type === 'half_float' ||
    type === 'scaled_float'
  ) {
    return 'number';
  }

  return null;
}

/**
 * Check if a field type supports URL formatting.
 * Currently enabled for text/keyword fields only.
 */
export function supportsUrlFormatting(fieldType: string, reportDatatype?: string): boolean {
  const type = (reportDatatype || fieldType || '').toLowerCase();
  return (
    type === 'text' ||
    type === 'keyword' ||
    type === 'string' ||
    type === '' ||
    // Catch typed suffixes like employee_id_keyword
    type.endsWith('keyword') ||
    type.endsWith('text')
  );
}
